/**
 * Edit Process Dialog
 * 
 * Reusable dialog component for editing processes
 * Can be used in TransitionsProcessesView, Chat UI cards, and other components
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, message, Spin, Tabs, Card } from 'antd';
import { apiClient } from '../../services/apiClient';
import { ProcessType, DemandFlowType, DemandType } from '../../types/enums';
import type { PrimeProcess } from '../../types/primeModel';
import {
  ProcessParametersEditor,
  DemandCalculationRulesEditor,
  ValidationRulesEditor,
  ImprovementParametersEditor
} from '../Parameters';
import {
  ProcessParameters,
  DemandCalculationRules,
  ImprovementParameters,
  DurationBehaviorConfig
} from '../../types/processParameters';
import { ValidationRules } from '../../types/transitionParameters';
import { DURATION_BEHAVIOR_CATALOG } from '../../config/processDurationCatalog';

const { TextArea } = Input;

interface EditProcessDialogProps {
  visible: boolean;
  processId: string | null;
  artifactId: string;
  onClose: () => void;
  onSave?: () => void; // Optional callback after successful save
}

export const EditProcessDialog: React.FC<EditProcessDialogProps> = ({
  visible,
  processId,
  artifactId: _artifactId,
  onClose,
  onSave,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [_process, setProcess] = useState<PrimeProcess | null>(null);
  const [artifacts, setArtifacts] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('basic');
  
  // JSON field states
  const [processParams, setProcessParams] = useState<ProcessParameters>({});
  const [demandRules, setDemandRules] = useState<DemandCalculationRules>({});
  const [validationRules, setValidationRules] = useState<ValidationRules>({});
  const [improvements, setImprovements] = useState<ImprovementParameters>({});

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load all prime artifacts for the dropdown
      const allArtifacts: any[] = [];
      let skip = 0;
      const limit = 100;
      let hasMore = true;
      
      while (hasMore) {
        const artifactsResponse = await apiClient.get(`/prime-artifacts?skip=${skip}&limit=${limit}`);
        const artifacts = artifactsResponse.data;
        allArtifacts.push(...artifacts);
        hasMore = artifacts.length === limit;
        skip += limit;
      }
      
      // Sort artifacts alphabetically by name
      allArtifacts.sort((a, b) => a.name.localeCompare(b.name));
      setArtifacts(allArtifacts);
      
      // Load process data
      const processResponse = await apiClient.get(`/prime-processes/${processId}`);
      const processData = processResponse.data;
      setProcess(processData);
      
      // Set JSON field states
      const loadedParams = processData.process_parameters || {};
      console.log('[EditProcessDialog] Loaded process_parameters:', loadedParams);
      console.log('[EditProcessDialog] Duration behavior type:', loadedParams.duration_behavior_type);
      console.log('[EditProcessDialog] Generation mode:', loadedParams.artifact_generation_mode);
      setProcessParams(loadedParams);
      setDemandRules(processData.demand_calculation_rules || {});
      setValidationRules(processData.validation_rules || {});
      setImprovements(processData.improvement_parameters || {});
      
      // Populate form with basic process data
      form.setFieldsValue({
        name: processData.name,
        description: processData.description,
        prime_artifact_ref: processData.prime_artifact_ref,
        process_type: processData.process_type,
        demand_flow_type: processData.demand_flow_type,
        demand_type: processData.demand_type,
      });
    } catch (error) {
      console.error('Error loading process data:', error);
      message.error('Failed to load process data');
    } finally {
      setLoading(false);
    }
  };

  // Load process data when dialog opens
  useEffect(() => {
    if (visible && processId) {
      void loadData();
    }
  }, [visible, processId, loadData]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // Prepare update payload using state variables for JSON fields
      const updatePayload = {
        name: values.name,
        description: values.description,
        prime_artifact_ref: values.prime_artifact_ref,
        process_type: values.process_type,
        demand_flow_type: values.demand_flow_type,
        demand_type: values.demand_type,
        process_parameters: processParams,
        demand_calculation_rules: demandRules,
        validation_rules: validationRules,
        improvement_parameters: improvements,
      };

      console.log('[EditProcessDialog] Saving process with params:', processParams);
      console.log('[EditProcessDialog] Duration behavior:', processParams.duration_behavior_type);
      console.log('[EditProcessDialog] Generation mode:', processParams.artifact_generation_mode);

      // Update process
      await apiClient.put(`/prime-processes/${processId}`, updatePayload);
      
      message.success('Process updated successfully');
      onSave?.();
      onClose();
    } catch (error: any) {
      console.error('Error saving process:', error);
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('Failed to save process');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setActiveTab('basic');
    setProcessParams({});
    setDemandRules({});
    setValidationRules({});
    setImprovements({});
    onClose();
  };

  return (
    <Modal
      title="Edit Process"
      open={visible}
      onOk={handleSave}
      onCancel={handleCancel}
      confirmLoading={saving}
      width={900}
      okText="Save"
      cancelText="Cancel"
    >
      <Spin spinning={loading}>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={[
            {
              key: 'basic',
              label: 'Basic Info',
              children: (
            <Form
              form={form}
              layout="vertical"
            >
              <Form.Item
                label="Process Name"
                name="name"
                rules={[{ required: true, message: 'Please enter a process name' }]}
              >
                <Input placeholder="Enter process name" />
              </Form.Item>

              <Form.Item
                label="Description"
                name="description"
              >
                <TextArea rows={3} placeholder="Enter process description" />
              </Form.Item>

              <Form.Item
                label="Prime Artifact Owner"
                name="prime_artifact_ref"
                rules={[{ required: true, message: 'Please select a prime artifact owner' }]}
                tooltip="The prime artifact that owns this process"
              >
                <Select 
                  placeholder="Select prime artifact owner"
                  showSearch
                  filterOption={(input, option) => {
                    const label = option?.label || option?.children;
                    return String(label).toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {artifacts.map(artifact => (
                    <Select.Option key={artifact.id} value={artifact.id} label={artifact.name}>
                      {artifact.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Process Type"
                name="process_type"
                rules={[{ required: true, message: 'Please select a process type' }]}
              >
                <Select placeholder="Select process type">
                  <Select.Option value={ProcessType.DEFAULT}>Default (Unassigned)</Select.Option>
                  <Select.Option value={ProcessType.DEMAND}>Demand</Select.Option>
                  <Select.Option value={ProcessType.SUPPLY}>Supply</Select.Option>
                  <Select.Option value={ProcessType.DELIVERY}>Delivery</Select.Option>
                  <Select.Option value={ProcessType.FINANCE}>Finance</Select.Option>
                  <Select.Option value={ProcessType.HR}>HR</Select.Option>
                  <Select.Option value={ProcessType.IT}>IT</Select.Option>
                  <Select.Option value={ProcessType.STRATEGY}>Strategy</Select.Option>
                  <Select.Option value={ProcessType.IMPROVEMENT}>Improvement</Select.Option>
                  <Select.Option value={ProcessType.QUALITY}>Quality</Select.Option>
                  <Select.Option value={ProcessType.COMPLIANCE}>Compliance</Select.Option>
                  <Select.Option value={ProcessType.RESEARCH}>Research</Select.Option>
                  <Select.Option value={ProcessType.MARKETFIT}>MarketFit</Select.Option>
                  <Select.Option value={ProcessType.FITFINDER}>FitFinder</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="Demand Flow Type"
                name="demand_flow_type"
                rules={[{ required: true, message: 'Please select a demand flow type' }]}
              >
                <Select placeholder="Select demand flow type">
                  <Select.Option value={DemandFlowType.PUSH}>Push</Select.Option>
                  <Select.Option value={DemandFlowType.PULL}>Pull</Select.Option>
                  <Select.Option value={DemandFlowType.DRUM}>Drum</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="Demand Type"
                name="demand_type"
                rules={[{ required: true, message: 'Please select a demand type' }]}
                tooltip="Defines how demand is set for this process"
              >
                <Select placeholder="Select demand type">
                  <Select.Option value={DemandType.SUPPLY}>Supply (default)</Select.Option>
                  <Select.Option value={DemandType.TARGET}>Target</Select.Option>
                  <Select.Option value={DemandType.CAPACITY}>Capacity</Select.Option>
                </Select>
              </Form.Item>
            </Form>
              )
            },
            {
              key: 'parameters',
              label: 'Process Parameters',
              children: (
            <ProcessParametersEditor
              parameters={processParams}
              onChange={setProcessParams}
              mode="company"
            />
              )
            },
            {
              key: 'demand',
              label: 'Demand Calculation',
              children: (
                <DemandCalculationRulesEditor
                  rules={demandRules}
                  onChange={setDemandRules}
                />
              )
            },
            {
              key: 'validation',
              label: 'Validation Rules',
              children: (
                <ValidationRulesEditor
                  validationRules={validationRules}
                  onChange={setValidationRules}
                />
              )
            },
            {
              key: 'improvements',
              label: 'Improvements',
              children: (
                <ImprovementParametersEditor
                  improvements={improvements}
                  onChange={setImprovements}
                />
              )
            },
            {
              key: 'duration',
              label: 'Duration & Execution',
              children: (
                <div style={{ padding: '16px' }}>
              {/* Duration Behavior Section */}
              <Card title="Duration Behavior" size="small" style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                    Duration Behavior Type
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    value={processParams.duration_behavior_type?.value}
                    onChange={(value) => {
                      console.log('[EditProcessDialog] Duration behavior changed to:', value);
                      const updated = {
                        ...processParams,
                        duration_behavior_type: {
                          type: 'string' as const,
                          value: value,
                          default: value,
                          description: 'Process duration behavior'
                        },
                        duration_behavior_config: {}  // Reset config when type changes
                      };
                      console.log('[EditProcessDialog] Updated processParams:', updated);
                      setProcessParams(updated);
                    }}
                    placeholder="Select duration behavior..."
                    allowClear
                  >
                    {DURATION_BEHAVIOR_CATALOG.map(opt => (
                      <Select.Option key={opt.value} value={opt.value}>
                        <div>
                          <div>{opt.label}</div>
                          <div style={{ fontSize: '11px', color: '#888' }}>{opt.description}</div>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                    How this process determines its execution duration
                  </div>
                </div>

                {/* Fixed Period Config */}
                {processParams.duration_behavior_type?.value === 'fixed_period' && (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                        Period Type
                      </label>
                      <Select 
                        style={{ width: '100%' }}
                        value={processParams.duration_behavior_config?.period_type}
                        onChange={(value) => {
                          setProcessParams({
                            ...processParams,
                            duration_behavior_config: {
                              ...processParams.duration_behavior_config,
                              period_type: value
                            } as DurationBehaviorConfig
                          });
                        }}
                        placeholder="Select period..."
                      >
                        <Select.Option value="daily">Daily</Select.Option>
                        <Select.Option value="weekly">Weekly</Select.Option>
                        <Select.Option value="monthly">Monthly</Select.Option>
                        <Select.Option value="quarterly">Quarterly</Select.Option>
                        <Select.Option value="yearly">Yearly</Select.Option>
                      </Select>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                        Calendar/Config Reference
                      </label>
                      <Input 
                        value={processParams.duration_behavior_config?.period_calendar_ref}
                        onChange={(e) => {
                          setProcessParams({
                            ...processParams,
                            duration_behavior_config: {
                              ...processParams.duration_behavior_config,
                              period_calendar_ref: e.target.value
                            } as DurationBehaviorConfig
                          });
                        }}
                        placeholder="Config artifact ID or path (e.g., accounting_config.fiscal_calendar)" 
                      />
                      <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                        Reference to configuration artifact defining period boundaries
                      </div>
                    </div>
                  </>
                )}

                {/* Capacity Driven Config */}
                {processParams.duration_behavior_type?.value === 'capacity_driven' && (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                        Resource Reference
                      </label>
                      <Input 
                        value={processParams.duration_behavior_config?.resource_ref}
                        onChange={(e) => {
                          setProcessParams({
                            ...processParams,
                            duration_behavior_config: {
                              ...processParams.duration_behavior_config,
                              resource_ref: e.target.value
                            } as DurationBehaviorConfig
                          });
                        }}
                        placeholder="Resource artifact ID" 
                      />
                      <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                        Resource artifact that determines capacity
                      </div>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                        Capacity Constraint Field
                      </label>
                      <Input 
                        value={processParams.duration_behavior_config?.capacity_constraint_field}
                        onChange={(e) => {
                          setProcessParams({
                            ...processParams,
                            duration_behavior_config: {
                              ...processParams.duration_behavior_config,
                              capacity_constraint_field: e.target.value
                            } as DurationBehaviorConfig
                          });
                        }}
                        placeholder="e.g., max_throughput, available_hours" 
                      />
                      <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                        Field containing capacity limit
                      </div>
                    </div>
                  </>
                )}

                {/* Event Driven Config */}
                {processParams.duration_behavior_type?.value === 'event_driven' && (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                        Start Event
                      </label>
                      <Input 
                        value={processParams.duration_behavior_config?.start_event}
                        onChange={(e) => {
                          setProcessParams({
                            ...processParams,
                            duration_behavior_config: {
                              ...processParams.duration_behavior_config,
                              start_event: e.target.value
                            } as DurationBehaviorConfig
                          });
                        }}
                        placeholder="e.g., invoice_created, order_placed" 
                      />
                      <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                        Event that triggers process start
                      </div>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                        End Event
                      </label>
                      <Input 
                        value={processParams.duration_behavior_config?.end_event}
                        onChange={(e) => {
                          setProcessParams({
                            ...processParams,
                            duration_behavior_config: {
                              ...processParams.duration_behavior_config,
                              end_event: e.target.value
                            } as DurationBehaviorConfig
                          });
                        }}
                        placeholder="e.g., invoice_sent, order_shipped" 
                      />
                      <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                        Event that triggers process end
                      </div>
                    </div>
                  </>
                )}
              </Card>

              <Card size="small" style={{ marginTop: 16, backgroundColor: '#f9fafb' }}>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  <strong>Note:</strong> Artifact Generation Mode has been moved to transition parameters. 
                  Configure it in the Edit Transition Dialog for CREATION transitions.
                </div>
                </Card>
                </div>
              )
            }
          ]}
        />
      </Spin>
    </Modal>
  );
};

